/* eslint-disable @typescript-eslint/no-unused-vars */

import { getColorValue } from '../../utils';

const objectBorderKeys = ['border', 'borderTop', 'borderBottom', 'borderLeft', 'borderRight'];
const objectColorKeys = [
  'color',
  'backgroundColor',
  'borderColor',
  'borderTopColor',
  'borderBottomColor',
  'borderLeftColor',
  'borderRightColor',
];

const templateBorderKeys = ['border', 'border-top', 'border-bottom', 'border-left', 'border-right'];
const templateColorKeys = [
  'color',
  'background-color',
  'border-color',
  'border-top-color',
  'border-bottom-color',
  'border-left-color',
  'border-right-color',
];

/**
 * Emotion 스타일 처리를 위한 내부 함수. 태그에 부착된 Emotion 스타일을 처리하는 기능.
 * @param tag - 처리 대상 태그
 * @param processor - 스타일 배열을 처리하는 함수
 */
export const internal_processStyles = (tag, processor) => {
  // Emotion attaches all the styles as `__emotion_styles`.
  // Ref: https://github.com/emotion-js/emotion/blob/16d971d0da229596d6bcc39d282ba9753c9ee7cf/packages/styled/src/base.js#L186
  if (Array.isArray(tag.__emotion_styles)) {
    tag.__emotion_styles = processor(tag.__emotion_styles);
  }
};

/**
 * 객체의 비어있음 여부 확인 기능.
 * @param obj - 확인 대상 객체
 * @returns 객체가 비어 있으면 true, 그렇지 않으면 false 반환
 */
export function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}

/**
 * 주어진 태그가 소문자로 시작하는 문자열인지 확인하는 기능.
 * @param tag - 검사 대상 태그
 * @returns 태그가 소문자로 시작하는 문자열이면 true, 그렇지 않으면 false 반환
 * 참고 - https://github.com/emotion-js/emotion/blob/26ded6109fcd8ca9875cc2ce4564fee678a3f3c5/packages/styled/src/utils.js#L40
 */
export function isStringTag(tag) {
  return typeof tag === 'string' && tag.charCodeAt(0) > 96;
}

/**
 * 속성 전달 여부 결정 기능.
 * @param prop - 검사 대상 속성 이름
 * @returns 속성이 전달되어야 하면 true, 그렇지 않으면 false 반환
 */
export function shouldForwardProp(prop) {
  return prop !== 'ownerState' && prop !== 'theme' && prop !== 'sx' && prop !== 'as';
}

/**
 * 문자열의 첫 글자를 소문자로 변환하는 기능.
 * @param string - 변환 대상 문자열
 * @returns 첫 글자가 소문자로 변환된 문자열
 */
export const lowercaseFirstLetter = (string) => {
  if (!string) {
    return string;
  }
  return string.charAt(0).toLowerCase() + string.slice(1);
};

/**
 * kebab-case -> camel-case
 */
const kebabToCamel = (str) => str.replaceAll(/-./g, (m) => m.toUpperCase()[1]);

/**
 * 주어진 테마의 비어있음 여부에 따라 기본 테마 혹은 주어진 테마 반환 기능.
 * @param defaultTheme - 기본 테마
 * @param theme - 검사 대상 테마
 * @returns 비어 있지 않은 테마
 */
export function resolveTheme({ defaultTheme, theme }) {
  return isEmpty(theme) ? defaultTheme : theme;
  // return systemDefaultTheme;
}

/**
 * 슬롯 기반의 기본 오버라이드 해결 기능.
 * @param slot - 검사 대상 슬롯
 * @returns 슬롯에 대한 스타일 처리 함수
 */
export function defaultOverridesResolver(slot) {
  if (!slot) {
    return null;
  }
  return (props, styles) => styles[slot];
}

/**
 * 스타일 인자 내의 색상 코드를 16진수 형태로 변환합니다.
 * color와 backgroundColor 속성이 있을 경우, 해당 색상 값을
 * 주어진 테마에 따라 16진수 색상 코드로 변경합니다.
 *
 * @param {Object} styleArgs - 변환할 스타일 인자 객체
 * @param {Object} theme - 색상 코드 변환에 사용될 테마 객체
 * @returns {Object} 변환된 스타일 인자 객체
 */
export function changeColorCodeToHex(styleArgs, theme) {
  if (styleArgs && typeof styleArgs === 'number') {
    return styleArgs;
  }
  if (styleArgs && typeof styleArgs === 'string') {
    return getColorValue(styleArgs, theme);
  }

  const copy = { ...styleArgs };

  objectBorderKeys.forEach((key) => {
    if (copy[key]) {
      const [size, shape, color] = copy[key].split(' ');
      if (size && shape && color) copy[key] = `${size} ${shape} ${getColorValue(color, theme)}`;
    }
  });

  objectColorKeys.forEach((key) => {
    if (copy[key]) {
      copy[key] = getColorValue(copy[key], theme);
    }
  });

  return copy;
}

function processCSS(style, theme) {
  const cssLines = style
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line);

  let convertStyle = `\n`;
  cssLines.forEach((line) => {
    let newLine = line;

    const splitNewLine = newLine.split(':');
    if (splitNewLine && splitNewLine.length > 1) {
      const key = splitNewLine[0];
      const value = splitNewLine[1].replaceAll(';', '').trim();

      if (templateBorderKeys.includes(key)) {
        const [size, shape, color] = value.split(' ');
        if (size && shape && color)
          newLine = `${key}: ${size} ${shape} ${getColorValue(color, theme)};\n`;
      }

      if (templateColorKeys.includes(key)) {
        if (value) newLine = `${key}: ${getColorValue(value, theme)};\n`;
      }
    }

    convertStyle += `${newLine}\n`;
  });

  return convertStyle;
}

/**
 * 스타일 인자 처리 기능. 함수 또는 객체 형태의 스타일 인자를 처리하여 스타일을 적용.
 * @param callableStyle - 호출 가능한 스타일 혹은 스타일 객체
 * @param props - 스타일 적용에 사용될 속성들
 * @returns 적용된 스타일 결과
 */
export function processStyleArg(callableStyle, { ownerState, ...props }) {
  // 스타일 인자가 함수인 경우 실행, 아니면 그대로 반환
  const resolvedStylesArg =
    typeof callableStyle === 'function' ? callableStyle({ ownerState, ...props }) : callableStyle;

  // 반환된 스타일이 배열인 경우, 각 요소를 재귀적으로 처리
  if (Array.isArray(resolvedStylesArg)) {
    return resolvedStylesArg.flatMap((resolvedStyle) => {
      return processStyleArg(resolvedStyle, { ownerState, ...props });
    });
  }

  // 반환된 스타일이 객체이고, variants 배열을 포함하는 경우
  if (
    !!resolvedStylesArg &&
    typeof resolvedStylesArg === 'object' &&
    Array.isArray(resolvedStylesArg.variants)
  ) {
    // 기본 스타일과 변형 스타일 분리
    const { variants = [], ...otherStyles } = resolvedStylesArg;
    let result = otherStyles;
    result = changeColorCodeToHex(result, props.theme);

    variants.forEach((variant) => {
      let isMatch = true;
      // variant의 props가 함수인 경우 실행, 아니면 키 값 비교
      if (typeof variant.props === 'function') {
        isMatch = variant.props({ ownerState, ...props, ...ownerState });
      } else {
        Object.keys(variant.props).forEach((key) => {
          if (ownerState?.[key] !== variant.props[key] && props[key] !== variant.props[key]) {
            isMatch = false;
          }
        });
      }

      if (isMatch) {
        if (!Array.isArray(result)) {
          result = [result];
        }

        result.push(() => {
          const style =
            typeof variant.style === 'function'
              ? variant.style({ ownerState, ...props, ...ownerState })
              : variant.style;

          const styled = changeColorCodeToHex(style, props.theme);
          return styled;
        });
      }
    });

    return result;
  }

  // css 함수를 이용하여 스타일을 작성했을 떄
  if (
    resolvedStylesArg?.styles &&
    resolvedStylesArg.styles &&
    typeof resolvedStylesArg.styles === 'string'
  ) {
    const convertStyle = processCSS(resolvedStylesArg.styles, props.theme);

    return { ...resolvedStylesArg, styles: convertStyle };
  }

  return changeColorCodeToHex(resolvedStylesArg, props.theme);
}
